import { useEffect, useMemo, useRef, useState } from 'react';
import type { LngLat } from '../lib/types.ts';
import {
  formatKm,
  loadDirectory,
  searchDirectory,
  STATE_NAMES,
  type Directory,
  type SearchHit,
} from '../lib/directory.ts';

interface Action {
  label: string;
  detail?: string;
  active?: boolean;
  onPick: () => void;
}

interface Props {
  open: boolean;
  onClose: () => void;
  at: LngLat | null;
  onPickCourse: (hit: SearchHit) => void;
  actions: Action[];
}

/**
 * One entry point for everything that is not the map.
 *
 * Replaces four tabs and three bar buttons. A driving surface should carry the
 * controls you need while moving and nothing else — everything occasional lives
 * one tap away instead of permanently covering the hole.
 */
export default function CommandSheet({ open, onClose, at, onPickCourse, actions }: Props) {
  const [dir, setDir] = useState<Directory | null>(null);
  const [query, setQuery] = useState('');
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    loadDirectory().then(setDir).catch(() => {});
    // Focus after the sheet has painted, or the keyboard fights the animation.
    const id = window.setTimeout(() => input.current?.focus(), 120);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const hits = useMemo(() => {
    if (!dir || query.trim().length < 2) return [];
    return searchDirectory(dir, {
      query,
      near: at ? at : null,
      limit: 12,
    });
  }, [dir, query, at]);

  const nearby = useMemo(() => {
    if (!dir || !at || query.trim()) return [];
    return searchDirectory(dir, { near: at, limit: 5 });
  }, [dir, at, query]);

  if (!open) return null;

  const list: SearchHit[] = query.trim() ? hits : nearby;

  return (
    <div className="sheet-scrim" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()} role="dialog">
        <div className="sheet-head">
          <input
            ref={input}
            className="sheet-search"
            value={query}
            placeholder={dir ? `Search ${dir.count.toLocaleString()} courses` : 'Search courses'}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
          />
          <button className="sheet-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {list.length > 0 && (
          <>
            <div className="sheet-label">{query.trim() ? 'COURSES' : 'NEAREST TO YOU'}</div>
            <div className="sheet-list">
              {list.map((c) => (
                <button
                  key={c.id}
                  className="sheet-row"
                  onClick={() => {
                    onPickCourse(c);
                    onClose();
                  }}
                >
                  <span className="r-name">{c.name}</span>
                  <span className="r-meta">
                    {[c.suburb, STATE_NAMES[c.state] ?? c.state].filter(Boolean).join(' · ')}
                    {c.distanceM != null ? ` · ${formatKm(c.distanceM)}` : ''}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {query.trim().length >= 2 && !list.length && (
          <div className="sheet-empty">No course matches “{query}”.</div>
        )}

        <div className="sheet-label">ACTIONS</div>
        <div className="sheet-actions">
          {actions.map((a) => (
            <button
              key={a.label}
              className={`sheet-action ${a.active ? 'on' : ''}`}
              onClick={() => {
                a.onPick();
                onClose();
              }}
            >
              <span>{a.label}</span>
              {a.detail && <span className="a-detail">{a.detail}</span>}
            </button>
          ))}
        </div>

        <div className="sheet-hint">
          Tip: tap the microphone and say a course name, “hole 5”, or “park here”.
        </div>
      </div>
    </div>
  );
}
